import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ConversationMessage, MessageThreadAttestationState } from '../types';
import {
  attestationRequestPendingText, formatMessageTime, isConversationMessage, originOf, sideOfRole, subjectKey, type ThreadSide
} from '../utils/messageThreads';
import { MessageAttachmentPicker } from './MessageAttachmentPicker';
import { MessageAttachments } from './MessageAttachments';

export interface ConversationSubject {
  key: string;
  label: string;
  /** e.g. "#12 Badrumsrenovering"; empty when the conversation has one project, named in its header. */
  project: string;
  icon: string;
  href: string;
  /** The other party has written something unread about it. */
  unread: boolean;
  /** A stage subject: its attestation right now (ClickUp 869fm3fy8). */
  attestation?: {
    state: MessageThreadAttestationState | null;
    milestoneNumber: number | null;
    milestoneId: string;
    contractId: string | null;
    contractNumber: number | null;
    /** The customer has not read the previous request and it is under 24 hours old. */
    requestPending?: boolean;
    /** When the latest request was sent, and when a new one may be sent (ClickUp 869fmfkyz). */
    latestRequestAt?: string | null;
    nextRequestAllowedAt?: string | null;
  };
}

interface Props {
  messages: ConversationMessage[];
  subjects: ConversationSubject[];
  mySide: ThreadSide;
  counterpartName: string;
  /** The subject a reply is about until the user picks another. */
  defaultAbout: string;
  /** How many of the other party's latest messages were unread when the conversation opened. */
  unreadCount: number;
  /** When the other party last read, for the receipt under my latest message. */
  counterpartReadAt: string | null;
  sending: boolean;
  error?: string | null;
  /** Rejects only when the message itself could not be sent. Files are sent
   * with it (ClickUp 869fj6n1d). */
  onSend: (message: string, about: string, files: File[], options?: { requestAttestation?: boolean }) => Promise<void>;
  /** The customer's "Granska och attestera" on an attestation request. */
  onOpenAttestation?: (subject: ConversationSubject) => void;
  /** One subject tab is chosen: no subject lines in the list, and no "Gäller"
   * — the reply goes to that subject, named in the bar above the reply box. */
  hideSubjectLines?: boolean;
}

/**
 * "Öppna etappen / kontraktet / projektet": the subject's own page, as a clear
 * outlined button (ClickUp 869fm3fy8). Shared with the bar above a chosen subject.
 */
export function SubjectOpenLink({ subject }: { subject: Pick<ConversationSubject, 'key' | 'href'> }) {
  const { t } = useTranslation();
  const type = subject.key.split(':')[0];
  return (
    <Link to={subject.href} className="message-subject-open" data-testid="message-subject-open">
      {t(`messageThreads.openSubjectType.${type}`, { defaultValue: t('messageThreads.openSubject') })}
      <i className="feather-arrow-right" aria-hidden="true"></i>
    </Link>
  );
}

/**
 * One conversation with a counterpart as a chat (ClickUp 869fegbvk, 869feg849):
 * every stage, contract and project in it, oldest at the top and newest at the
 * bottom, a subject line where the subject changes and the reply box under
 * them with "Gäller". The same component for both parties. A message's own
 * text is never translated.
 */
export function MessageConversation({
  messages, subjects, mySide, counterpartName, defaultAbout, unreadCount, counterpartReadAt, sending, error, onSend, onOpenAttestation, hideSubjectLines = false
}: Props) {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const listRef = useRef<HTMLOListElement>(null);
  const composeRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  // "Be om attest" (ClickUp 869fm3fy8): only the company, only on a stage
  // that awaits the customer's attestation; unticked when the subject changes.
  const [requestAttestation, setRequestAttestation] = useState(false);
  const [about, setAbout] = useState(defaultAbout);
  useEffect(() => { setAbout(defaultAbout); }, [defaultAbout]);
  useEffect(() => { setRequestAttestation(false); }, [about]);
  const conversation = messages.filter(message => isConversationMessage(message, message.threadType));
  const subjectByKey = new Map(subjects.map(subject => [subject.key, subject]));
  const otherSide: ThreadSide = mySide === 'person' ? 'company' : 'person';
  const aboutAttestation = subjectByKey.get(about)?.attestation;
  const canRequestAttestation = mySide === 'company' && aboutAttestation?.state === 'awaiting_person' && !aboutAttestation.requestPending;
  // A request the customer has not read yet blocks the next one for 24 hours (no spam).
  const requestBlocked = mySide === 'company' && aboutAttestation?.state === 'awaiting_person' && !!aboutAttestation.requestPending;
  const toggleRequestAttestation = (checked: boolean) => {
    setRequestAttestation(checked);
    // A ready, editable text, only when nothing is written yet.
    if (checked && !draft.trim()) setDraft(t('messageThreads.attestationRequest.prefill', { number: aboutAttestation?.milestoneNumber ?? '' }));
  };

  // What an attestation request shows under its text: the button for the
  // customer while the stage awaits them, otherwise the stage's state now.
  const attestationRequestStatus = (subject: { attestation?: ConversationSubject['attestation']; href: string } | undefined) => {
    const attestation = subject?.attestation;
    if (!attestation) return null;
    const number = attestation.milestoneNumber ?? '';
    if (attestation.state === 'awaiting_person') {
      return mySide === 'person' && onOpenAttestation ? (
        <button type="button" className="btn btn-primary message-attestation-cta" data-testid="message-attestation-open"
          onClick={() => onOpenAttestation(subject as ConversationSubject)}>
          <i className="feather-check-circle" aria-hidden="true"></i>
          {t('messageThreads.attestationRequest.open', { number })}
          <i className="feather-arrow-right" aria-hidden="true"></i>
        </button>
      ) : <p className="message-attestation-status" data-testid="message-attestation-status">{t('messageThreads.attestationRequest.waiting', { number })}</p>;
    }
    if (attestation.state === 'attested') {
      return <p className="message-attestation-status is-done" data-testid="message-attestation-status">
        <i className="feather-check" aria-hidden="true"></i>{t('messageThreads.attestationRequest.attested', { number })}</p>;
    }
    if (attestation.state === 'objected') {
      return <p className="message-attestation-status is-objected" data-testid="message-attestation-status">
        <i className="feather-alert-triangle" aria-hidden="true"></i>{t('messageThreads.attestationRequest.objected', { number })}{' '}
        <Link to={subject!.href}>{t('messageThreads.attestationRequest.openCase')}</Link></p>;
    }
    return null;
  };

  // The "Nytt" line goes above the first of the other party's unread messages.
  let firstUnreadIndex = -1;
  if (unreadCount > 0) {
    let seen = 0;
    for (let index = conversation.length - 1; index >= 0; index -= 1) {
      if (sideOfRole(conversation[index].userRole) !== otherSide) continue;
      seen += 1;
      if (seen === unreadCount) { firstUnreadIndex = index; break; }
    }
  }
  const last = conversation[conversation.length - 1];
  const lastIsMine = !!last && sideOfRole(last.userRole) === mySide;
  const readByCounterpart = lastIsMine && !!counterpartReadAt
    && new Date(counterpartReadAt).getTime() >= new Date(last.createdAt).getTime();

  // Open at the newest message, as a chat does: inside the pane beside the
  // list, or by scrolling the page on a phone, where the thread has no pane.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
    if (list.scrollHeight <= list.clientHeight + 1 && window.innerWidth < 992) {
      composeRef.current?.scrollIntoView?.({ block: 'end' });
    }
  }, [conversation.length]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if ((!text && files.length === 0) || sending) return;
    try {
      await onSend(text, about, files, canRequestAttestation && requestAttestation ? { requestAttestation: true } : undefined);
    } catch {
      return; // The page shows the error; the draft and files stay for another try.
    }
    setDraft('');
    setFiles([]);
    setRequestAttestation(false);
  };

  const replyTo = (key: string) => {
    setAbout(key);
    inputRef.current?.focus();
  };

  let previousSubject: string | null = null;
  return (
    <div className="message-conversation">
      <ol className="message-conversation-list" ref={listRef} aria-label={t('messageThreads.conversation')} data-testid="message-conversation-list">
        {conversation.length === 0 && <li className="messages-empty">{t('messageThreads.noneInSubject')}</li>}
        {conversation.map((message, index) => {
          const side = sideOfRole(message.userRole);
          const mine = side === mySide;
          const key = subjectKey(message);
          const subject = subjectByKey.get(key);
          const showSubject = key !== previousSubject;
          previousSubject = key;
          return (
            <React.Fragment key={message.id}>
              {showSubject && subject && !hideSubjectLines && (
                <li className="message-subject-line" data-testid="message-subject-line">
                  <i className={`feather-${subject.icon}`} aria-hidden="true"></i>
                  <span>{subject.label}{subject.project && <small> · {subject.project}</small>}</span>
                  <SubjectOpenLink subject={subject} />
                </li>
              )}
              {index === firstUnreadIndex && <li className="message-unread-line" aria-label={t('messageThreads.newMessages')}>{t('messageThreads.new')}</li>}
              <li className={`message-bubble${mine ? ' is-mine' : ''}`} data-testid="message-bubble">
                <div className="message-bubble-meta">
                  <strong>{mine ? t('messageThreads.you') : message.userName}</strong>
                  <time dateTime={message.createdAt}>· {formatMessageTime(message.createdAt, i18n.language)}</time>
                  {/* Where it was written: at attestation, in an objection, when the contract was sent… */}
                  <span className="message-tag" data-testid="message-origin">{t(`messageThreads.origin.${originOf(message)}`)}</span>
                </div>
                {message.message && <p className="message-bubble-text">{message.message}</p>}
                <MessageAttachments attachments={message.attachments} />
                {originOf(message) === 'attestation_request' && attestationRequestStatus(subject)}
                {!mine && subject && (
                  <button type="button" className="message-reply-to" onClick={() => replyTo(key)} data-testid="message-reply-to">
                    {t('messageThreads.replyToThis')}
                  </button>
                )}
              </li>
            </React.Fragment>
          );
        })}
        {lastIsMine && (
          <li className="message-receipt" data-testid="message-receipt">
            {readByCounterpart ? t('messageThreads.readBy', { counterpart: counterpartName }) : t('messageThreads.notReadYet')}
          </li>
        )}
      </ol>
      <form className="message-compose" onSubmit={submit} ref={composeRef}>
        {/* In a chosen subject tab: what the reply is about, with the way to
            it, in place of "Gäller" (ClickUp 869fm3fy8). Under "Allt" the
            subject lines and "Gäller" say it. */}
        {hideSubjectLines && subjectByKey.get(about) && (() => {
          const chosen = subjectByKey.get(about)!;
          return (
            <div className="messages-subject-bar" data-testid="messages-subject-bar">
              <i className={`feather-${chosen.icon}`} aria-hidden="true"></i>
              <span>{chosen.label}{chosen.project && <small> · {chosen.project}</small>}</span>
              <SubjectOpenLink subject={chosen} />
            </div>
          );
        })()}
        {error && <div className="alert alert-danger mb-0" role="alert">{error}</div>}
        {!hideSubjectLines && (
          <div className="message-about">
            <label className="form-label fw-semibold mb-0" htmlFor={`${ids}-about`}>{t('messageThreads.about')}</label>
            <select
              id={`${ids}-about`}
              className="form-select"
              value={about}
              onChange={event => setAbout(event.target.value)}
              disabled={sending}
              data-testid="message-about"
            >
              {subjects.map(subject => (
                <option key={subject.key} value={subject.key}>
                  {subject.label}{subject.project ? ` · ${subject.project}` : ''}{subject.unread ? ` – ${t('messageThreads.state.unread')}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        <label className="form-label fw-semibold mb-0" htmlFor={`${ids}-reply`}>{t('messageThreads.replyTo', { counterpart: counterpartName })}</label>
        <textarea
          id={`${ids}-reply`}
          ref={inputRef}
          className="form-control"
          rows={2}
          placeholder={t('messageThreads.placeholder')}
          value={draft}
          onChange={event => setDraft(event.target.value)}
          disabled={sending}
          data-testid="message-reply-input"
        />
        {requestBlocked && (
          <p className="message-attestation-request is-blocked" data-testid="message-request-attestation-blocked">
            <i className="feather-clock" aria-hidden="true"></i>
            <span>{attestationRequestPendingText(t, i18n.language, {
              milestoneNumber: aboutAttestation?.milestoneNumber,
              latestRequestAt: aboutAttestation?.latestRequestAt,
              nextRequestAllowedAt: aboutAttestation?.nextRequestAllowedAt,
            })}</span>
          </p>
        )}
        {canRequestAttestation && (
          <label className="message-attestation-request" data-testid="message-request-attestation">
            <input type="checkbox" className="form-check-input" checked={requestAttestation} disabled={sending}
              onChange={event => toggleRequestAttestation(event.target.checked)} />
            <span>
              <strong>{t('messageThreads.attestationRequest.label', { number: aboutAttestation?.milestoneNumber ?? '' })}</strong>
              <small>{t('messageThreads.attestationRequest.hint')}</small>
            </span>
          </label>
        )}
        <div className="message-compose-row">
          <MessageAttachmentPicker files={files} onChange={setFiles} disabled={sending} />
          <button type="submit" className="btn btn-primary" disabled={sending || (!draft.trim() && files.length === 0) || !about} data-testid="message-send">
            {t(sending ? 'messageThreads.sending' : 'messageThreads.send')}
          </button>
        </div>
      </form>
    </div>
  );
}
